import React, { useEffect } from 'react';
import logger from '../utils/logger';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  children: React.ReactNode;
  isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  children, 
  isLoading = false,
  className = '',
  disabled,
  onClick,
  ...props 
}) => {
  useEffect(() => {
    logger.debug('Button', 'Button rendered', { variant, isLoading, disabled });
  }, [variant, isLoading, disabled]);

  const baseStyles = "px-6 py-3 rounded-xl font-heading text-base tracking-luxury-tight transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-xl";
  
  const variantStyles = {
    primary: "glass-grain border-2 border-cosmic-gold/50 text-white hover:border-cosmic-gold hover:bg-cosmic-gold/10 hover:scale-[1.02]",
    secondary: "glass-grain border border-cosmic-gold/40 text-white hover:border-cosmic-gold/60 bg-space-dark/40 hover:bg-space-dark/60 hover:scale-[1.02]",
    ghost: "text-text-secondary hover:text-text-primary"
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    logger.userAction('Button', 'Button clicked', { variant, isLoading, disabled: disabled || isLoading });
    if (onClick && !disabled && !isLoading) {
      onClick(e);
    } else if (disabled || isLoading) {
      logger.debug('Button', 'Button click ignored', { reason: disabled ? 'disabled' : 'loading' });
    }
  };
  
  const buttonStyle = variant === 'primary' || variant === 'secondary' ? {
    backgroundColor: variant === 'primary' 
      ? 'rgba(20, 20, 32, 0.3)' 
      : 'rgba(20, 20, 32, 0.3)',
    boxShadow: variant === 'primary'
      ? '0 0 25px rgba(255, 184, 79, 0.3), 0 8px 32px rgba(0,0,0,0.4)'
      : '0 8px 32px rgba(0,0,0,0.4)'
  } : {};

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      style={buttonStyle}
      disabled={disabled || isLoading}
      onClick={handleClick}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading...
        </span>
      ) : children}
    </button>
  );
};

export default Button;

